import type { MetricColumnConfig } from '../metrics';
import type { FieldRule, QualifiedId } from '../fieldRulesConfig';
import type { CalendarBarColorOptionsSource, CalendarBarFieldMap } from '../calendarColumnsConfig';
import type { CustomerConfigResponse, EntityAttribute, MetricConfig } from '../../types/config';
import { validateRuleSet } from '../../rules/engine/validate';
import { conditionSources } from '../../rules/engine/evaluateCondition';
import { EDIT_PLAN_MENU_KEY, PLAN_ACTION_SURFACES } from '../planActionMenuConfig';
import { adaptMetrics } from './metricAdapter';
import { adaptFeConfig } from './feConfigAdapter';
import type { FeDerivedConfig } from './feConfigAdapter';
import { adaptPromoLibraryConfig } from './promoLibraryAdapter';
import type { PromoLibraryConfig } from '../promoLibraryConfig';

const METRIC_PREFIX = 'metric:';

const targetsOf = (rule: FieldRule): QualifiedId[] => (Array.isArray(rule.target) ? rule.target : [rule.target]);

const metricTargetsOf = (rule: FieldRule): string[] =>
    targetsOf(rule)
        .filter((t) => t.startsWith(METRIC_PREFIX))
        .map((t) => t.slice(METRIC_PREFIX.length));

const refsOf = (rule: FieldRule): QualifiedId[] => [
    ...targetsOf(rule),
    ...conditionSources(rule.when),
    ...(rule.triggers ?? []),
];

const addFieldNames = (known: Set<string>, scope: string, fields: ReadonlyArray<{ name?: string }>): void => {
    for (const field of fields) if (field?.name) known.add(`${scope}:${field.name}`);
};

/**
 * Every qualified id the engine can legitimately resolve, used to flag typo'd
 * metric-rule targets/sources. Built from the backend metric keys (`metric:*`),
 * the FE form-field configs per scope, and every id referenced by the authored
 * `fieldRules` registry (the latter covers dim/composed field ids such as
 * `event:dim1-brand` that are not plain `.name` entries).
 *
 * Exported so `scripts/validateTpoRules.ts` (standalone rule authoring
 * validation, outside the running app) can run the same `fieldRules`
 * known-id check this module applies to `metricRules`/`formCascadeRules`.
 */
export function buildKnownIds(fe: FeDerivedConfig, metrics: MetricConfig[]): Set<string> {
    const known = new Set<string>();
    for (const metric of metrics) known.add(`${METRIC_PREFIX}${metric.key}`);
    addFieldNames(known, 'promo', fe.promoFields as ReadonlyArray<{ name?: string }>);
    addFieldNames(known, 'event', fe.eventDetailsFields as ReadonlyArray<{ name?: string }>);
    addFieldNames(known, 'event', fe.promoDetailsFields as ReadonlyArray<{ name?: string }>);
    addFieldNames(known, 'plan', fe.planFields as ReadonlyArray<{ name?: string }>);
    for (const rule of fe.fieldRules) {
        for (const ref of refsOf(rule)) if (!ref.startsWith('ctx:')) known.add(ref);
    }
    return known;
}

/**
 * Dev-only sanity checks for the FE-authored `metricRules` registry. Non-blocking
 * (logs, never throws) so a stale rule cannot break the app. Surfaces the failure
 * modes from the design doc:
 * - malformed / duplicate rule ids (shared rule validator);
 * - fail-OPEN: a `metric:<key>` target matching no backend metric (typo / rename)
 *   -> the real metric stays always-applicable;
 * - fail-CLOSED: a condition `source` that resolves to no known field id (e.g.
 *   `promo:promotionType`) -> the metric is hidden for every promo type;
 *   both are caught by passing `knownIds` to the validator;
 * - duplicate rules targeting the same metric (restrictive-wins surprises);
 * - drift: backend metrics carrying `applicable_when` with no FE rule.
 */
function devValidateMetricRules(fe: FeDerivedConfig, metrics: MetricConfig[]): void {
    if (process.env.NODE_ENV === 'production') return;

    const metricRules = fe.metricRules;
    const knownIds = buildKnownIds(fe, metrics);

    const issues = validateRuleSet(metricRules, knownIds);
    if (issues.length > 0) {
        // eslint-disable-next-line no-console
        console.error('[metricRules] validation issues (unknown/malformed/duplicate ids):', issues);
    }

    const seen = new Set<string>();
    const duplicateTargets = new Set<string>();
    const ruledKeys = new Set<string>();
    for (const rule of metricRules) {
        for (const key of metricTargetsOf(rule)) {
            ruledKeys.add(key);
            if (seen.has(key)) duplicateTargets.add(key);
            seen.add(key);
        }
    }
    if (duplicateTargets.size > 0) {
        // eslint-disable-next-line no-console
        console.error('[metricRules] multiple rules target the same metric:', [...duplicateTargets]);
    }

    const drift = metrics.filter((m) => m.applicable_when?.length && !ruledKeys.has(m.key)).map((m) => m.key);
    if (drift.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('[metricRules] backend metrics with applicable_when but no FE rule:', drift);
    }
}

/** Recursively flags `in`/`notIn` conditions whose `value` is not an array (they would never match). */
function collectArrayOpIssues(cond: unknown, ruleId: string, issues: string[]): void {
    if (!cond || typeof cond !== 'object') return;
    const c = cond as Record<string, unknown>;
    if ('source' in c) {
        if ((c.op === 'in' || c.op === 'notIn') && !Array.isArray(c.value)) {
            issues.push(
                `rule "${ruleId}" condition on "${String(c.source)}" uses ${String(c.op)} with a non-array value`,
            );
        }
        return;
    }
    for (const key of ['all', 'any'] as const) {
        const arr = c[key];
        if (Array.isArray(arr)) arr.forEach((sub) => collectArrayOpIssues(sub, ruleId, issues));
    }
    if (c.not) collectArrayOpIssues(c.not, ruleId, issues);
}

/**
 * Dev-only sanity checks for the FE-authored `formCascadeRules` registry (the
 * deterministic form -> metric value cascade). Non-blocking (logs, never throws)
 * and mirrors `devValidateMetricRules`. Converts the silent-failure modes from
 * the design into loud dev errors:
 * - duplicate / missing rule ids;
 * - leftover removed `appliesToPromoTypes` (only reachable via a live `fe_config`
 *   passthrough; the FE `satisfies FormCascadeRule[]` guard already blocks it);
 * - `when` / variant condition `source`s must be `promo:` / `metric:` and known
 *   (catches typos + out-of-scope `event:*`/`plan:*`/`ctx:*`);
 * - `in` / `notIn` conditions whose `value` is not an array (never match);
 * - unknown `metricKey` target or unknown `inputs.form` / `unitField` promo fields.
 */
function devValidateFormCascadeRules(fe: FeDerivedConfig, metrics: MetricConfig[]): void {
    if (process.env.NODE_ENV === 'production') return;

    const rules = fe.formCascadeRules;
    const knownIds = buildKnownIds(fe, metrics);
    const metricKeys = new Set(metrics.map((m) => m.key));
    // A `unitField` (e.g. `discountUnit`) is a real, independently-valued form
    // field rendered alongside its parent (e.g. `discount`), but it is declared
    // as `parent.unitToggle.unitField`, not as its own top-level `PromoFieldConfig`
    // entry - so it must be added explicitly or every unit-toggle rule false-positives.
    const promoFields = fe.promoFields as ReadonlyArray<{ name?: string; unitToggle?: { unitField?: string } }>;
    const promoFieldNames = new Set(
        promoFields.flatMap((f) => [f.name, f.unitToggle?.unitField].filter((name): name is string => !!name)),
    );

    const issues: string[] = [];
    const seenIds = new Set<string>();

    for (const rule of rules) {
        if (!rule.id) issues.push('a rule is missing `id`');
        else if (seenIds.has(rule.id)) issues.push(`duplicate rule id "${rule.id}"`);
        if (rule.id) seenIds.add(rule.id);

        if ((rule as unknown as Record<string, unknown>).appliesToPromoTypes !== undefined) {
            issues.push(`rule "${rule.id}" still uses removed \`appliesToPromoTypes\` (migrate to \`when\`)`);
        }

        const whens = [rule.when, ...(rule.effect.variants ?? []).map((v) => v.when)];
        for (const when of whens) {
            for (const source of conditionSources(when)) {
                if (!source.startsWith('promo:') && !source.startsWith('metric:')) {
                    issues.push(`rule "${rule.id}" condition source "${source}" must be promo: or metric:`);
                } else if (!knownIds.has(source)) {
                    issues.push(`rule "${rule.id}" condition source "${source}" is not a known field/metric`);
                }
            }
            collectArrayOpIssues(when, rule.id, issues);
        }

        if (!metricKeys.has(rule.effect.metricKey)) {
            issues.push(`rule "${rule.id}" targets unknown metric "${rule.effect.metricKey}"`);
        }
        for (const field of rule.effect.inputs.form) {
            if (!promoFieldNames.has(field)) {
                issues.push(`rule "${rule.id}" inputs.form references unknown promo field "${field}"`);
            }
        }
        const unitFields = [rule.effect.unitField, ...(rule.effect.variants ?? []).map((v) => v.unitField)].filter(
            (u): u is string => !!u,
        );
        for (const unit of unitFields) {
            if (!promoFieldNames.has(unit)) {
                issues.push(`rule "${rule.id}" unitField references unknown promo field "${unit}"`);
            }
        }
    }

    if (issues.length > 0) {
        // eslint-disable-next-line no-console
        console.error('[formCascadeRules] validation issues:', issues);
    }
}

const COLOR_OPTION_KEYS: Record<CalendarBarColorOptionsSource, (raw: CustomerConfigResponse) => string[]> = {
    promotionTypes: (raw) => raw.promotion_types.map((type) => type.key),
    tactics: (raw) => raw.tactics.map((tactic) => tactic.key),
};

/**
 * Dev-only check that the calendar bar `colorMap` is keyed by values the backend
 * can actually return. Without it, a renamed/mis-cased key fails silently — the
 * bar just falls back to a generated colour with no signal.
 */
function devValidateCalendarBarColors(fieldMap: CalendarBarFieldMap, raw: CustomerConfigResponse): void {
    if (process.env.NODE_ENV === 'production') return;

    const colorMap = fieldMap.colorMap;
    if (!colorMap) return;

    const source = fieldMap.colorOptionsSource;
    if (!source) {
        // eslint-disable-next-line no-console
        console.warn('[calendarBarFieldMap] colorMap is set without colorOptionsSource — keys cannot be validated.');
        return;
    }

    const validKeys = new Set((COLOR_OPTION_KEYS[source]?.(raw) ?? []).map((key) => key.toLowerCase()));
    const unknown = Object.keys(colorMap).filter((key) => !validKeys.has(key.trim().toLowerCase()));
    if (unknown.length > 0) {
        // eslint-disable-next-line no-console
        console.error(`[calendarBarFieldMap] colorMap keys absent from ${source}:`, unknown);
    }

    const uncovered = [...validKeys].filter(
        (key) => !Object.keys(colorMap).some((mapKey) => mapKey.trim().toLowerCase() === key),
    );
    if (uncovered.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(`[calendarBarFieldMap] ${source} entries with no colorMap entry:`, uncovered);
    }
}

/**
 * Dev-only check on `planActionMenuViews`, whose failure modes are all silent:
 * a mistyped surface hides the item on every view, a mistyped key governs
 * nothing, and an item with no entry quietly shows up on all three.
 */
function devValidatePlanActionMenuViews(fe: FeDerivedConfig): void {
    if (process.env.NODE_ENV === 'production') return;

    const views = fe.planActionMenuViews;
    // An absent map is the documented "everything everywhere" default, so it is
    // only worth reporting the keys of a map that exists.
    if (Object.keys(views).length === 0) return;

    const itemKeys = new Set(
        fe.planActionMenuItems
            .flat()
            .flatMap((item) => (item && 'key' in item && typeof item.key === 'string' ? [item.key] : [])),
    );
    const validSurfaces = new Set<string>(PLAN_ACTION_SURFACES);

    const unknownSurfaces = [
        ...new Set(Object.values(views).flatMap((surfaces) => surfaces.filter((s) => !validSurfaces.has(s)))),
    ];
    if (unknownSurfaces.length > 0) {
        // eslint-disable-next-line no-console
        console.error('[planActionMenuViews] unknown surfaces (item will be hidden everywhere):', unknownSurfaces);
    }

    // `edit_plan` is injected by the edit lock rather than configured, so it is
    // a legitimate key with no matching item.
    const unknownKeys = Object.keys(views).filter((key) => key !== EDIT_PLAN_MENU_KEY && !itemKeys.has(key));
    if (unknownKeys.length > 0) {
        // eslint-disable-next-line no-console
        console.error('[planActionMenuViews] entries matching no planActionMenuItems key:', unknownKeys);
    }

    const undeclared = [...itemKeys].filter((key) => views[key] === undefined);
    if (undeclared.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('[planActionMenuViews] items with no entry — they render on every surface:', undeclared);
    }
}

/**
 * Drop-in replacement for the old static `tpoGenConfig` object: the
 * `fe_config`-derived static UI config merged with the API-derived editable
 * metric clusters. Exposed through `CustomerConfigContext` via
 * `useTpoGenConfig()`.
 */
export type TpoGenConfig = FeDerivedConfig & {
    promoLibraryConfig: PromoLibraryConfig | null;
    promoMetricsConfig: MetricColumnConfig[];
    promoSpendsConfig: MetricColumnConfig[];
    /**
     * `fe_hidden` metrics outside the editable grid groups, kept purely to feed
     * the promo grid cascade DAG (never displayed). See `AdaptedMetrics`.
     */
    formulaOnlyMetricsConfig: MetricColumnConfig[];
    /** Tab label for the Metrics cluster — from `metric_groups` `promo_metrics` display_name. */
    promoMetricsGroupLabel: string;
    /** Tab label for the Spends cluster — from `metric_groups` `spend_metrics` display_name. */
    promoSpendsGroupLabel: string;
    // Generic business-attribute schema (top-level in the config response, not
    // in `fe_config`). Drives the dynamic `attributes` codec.
    entityAttributes: EntityAttribute[];
};

export function parseTpoGenConfig(raw: CustomerConfigResponse): TpoGenConfig {
    const {
        promoMetricsConfig,
        promoSpendsConfig,
        formulaOnlyMetricsConfig,
        promoMetricsGroupLabel,
        promoSpendsGroupLabel,
    } = adaptMetrics(raw.metrics, raw.metric_groups);
    const fe = adaptFeConfig(raw.fe_config);
    const promoLibraryConfig = adaptPromoLibraryConfig(
        raw.promo_library,
        fe.promoLibraryUiConfig,
        fe.fieldValueLabelMaps,
    );
    devValidateMetricRules(fe, raw.metrics);
    devValidateFormCascadeRules(fe, raw.metrics);
    devValidateCalendarBarColors(fe.calendarBarFieldMap, raw);
    devValidatePlanActionMenuViews(fe);
    return {
        ...fe,
        promoLibraryConfig,
        promoMetricsConfig,
        promoSpendsConfig,
        formulaOnlyMetricsConfig,
        promoMetricsGroupLabel,
        promoSpendsGroupLabel,
        entityAttributes: raw.entity_attributes,
    };
}
